"use client";

import React from "react";

import { cn } from "@/lib/utils";

type BadgeVariant = "navigation" | "status" | "urgency";
type BadgeSize = "sm" | "md";

interface OrgBadgeProps {
  /**
   * Badge variant determines styling
   * - navigation: For navigation badges (e.g., "Needs 60")
   * - status: For status indicators
   * - urgency: For urgency indicators
   */
  variant?: BadgeVariant;
  /**
   * Badge size
   * - sm: text-xs
   * - md: text-sm
   */
  size?: BadgeSize;
  /**
   * Badge content
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Reusable badge component for navigation badges, status badges, urgency badges
 * Uses org primary color via CSS variables
 */
export function OrgBadge({
  children,
  className,
  size = "sm",
  variant = "navigation",
}: OrgBadgeProps) {
  const sizeClass = size === "sm" ? "text-xs" : "text-sm";

  // Base classes for all badges
  const baseClasses = "font-medium rounded-full px-1.5 py-0.5";

  // Variant-specific classes
  const variantClasses =
    variant === "navigation"
      ? "bg-primary text-primary-foreground"
      : variant === "status"
        ? "bg-primary-100 text-primary-900"
        : "bg-primary-200 text-primary-900";

  return (
    <span className={cn(baseClasses, sizeClass, variantClasses, className)}>
      {children}
    </span>
  );
}
